import { useState } from 'react';

/**
 * Validates if a string represents a valid numeric format for float input.
 * Matches: optional minus, digits, optional decimal point with digits.
 */
export function isValidNumericString(value: string): boolean {
  const numericPattern = /^-?\d*\.?\d*$/;
  return numericPattern.test(value);
}

/**
 * Creates a change handler for float input with range validation.
 * Returns the validated value or null if the input should be rejected.
 */
export function validateFloatInput(
  inputValue: string,
  min: number,
  max: number,
): { valid: true; value: string | number } | { valid: false } {
  // Allow empty or minus sign for typing
  if (inputValue === '' || inputValue === '-') {
    return { valid: true, value: inputValue };
  }

  // Validate that the entire string is a valid number format
  if (!isValidNumericString(inputValue)) {
    return { valid: false };
  }

  // Allow partial input like "36." for typing decimals
  if (inputValue.endsWith('.')) {
    return { valid: true, value: inputValue };
  }

  const numericValue = parseFloat(inputValue);
  if (!Number.isNaN(numericValue) && numericValue >= min && numericValue <= max) {
    return { valid: true, value: inputValue };
  }

  return { valid: false };
}

export function useNumberInput(initialValue: number, name?: string, float = false) {
  const [value, setValue] = useState(initialValue);

  return {
    value,
    setValue,
    reset: () => setValue(initialValue),
    bind: {
      value,
      name,
      onChange: (event: any) => {
        setValue(float ? parseFloat(event.target.value) : parseInt(event.target.value, 10));
      },
      setValue: (val: number) => setValue(val),
    },
  };
}

export function useFloatInput(initialValue: number | string, min: number, max: number, name?: string) {
  const [value, setValue] = useState<string | number>(initialValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = validateFloatInput(e.target.value, min, max);
    if (result.valid) {
      setValue(result.value);
    }
  };

  return {
    value,
    setValue,
    reset: () => setValue(initialValue),
    bind: {
      name,
      value,
      onChange: handleChange,
      setValue: (val: number) => setValue(val),
    },
  };
}
